import React, { useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { addVanCreditAmount } from '../../../app/common/slices/vanBalanceSlice';
import poweredByLogo from '../../../assets/images/exim-bank-tanzania.png';
import useDataCountry from '../../../hooks/useDataCountry';

const ACCENT_ORANGE = '#f5822a';

/** Demo/static copy — VAN Amount comes from Redux `vanBalance` (same as navbar). */
const DEMO = {
  vanAccountNo: 'VANSHF10000301 (Demo)',
  banks: [{ value: 'qnb', label: 'SHERAF NATIONAL BANK(DJ)' }],
  accountName: 'Sheraf W.L.L',
  accountNo: 'DJ60QNBA000000000220015628001',
  iban: 'DJ60QNBA000000000220015628001',
  swiftAddress: '',
  supportEmail: 'global.support@odex.co',
};

const STEPS = [
  'Step 1: Note Bank details from this screen.',
  'Step 2: Transfer funds online to the bank on details mentioned.',
  'Step 3: While transferring/depositing mention VAN account no in reference.',
  'Step 4: Once Done, Upload Receipt here and Submit. You are done!',
];

/**
 * @param {{ show: boolean; onHide: () => void }} props
 */
const VanAccountModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const vanBalance = useSelector((state) => state.vanBalance.balance);
  const storedVanCurrency = useSelector((state) => state.vanBalance.currency);
  const dataCountry = useDataCountry();
  const vanCurrency =
    String(dataCountry).toUpperCase() === 'AE' ? 'AED' : storedVanCurrency;

  const vanAmountDisplay = Number.isFinite(vanBalance)
    ? vanBalance.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : '0';

  const [bankId, setBankId] = useState(DEMO.banks[0].value);
  const [creditAmount, setCreditAmount] = useState('');
  const [fileLabel, setFileLabel] = useState('');
  const fileRef = useRef(null);

  const resetAndClose = () => {
    setCreditAmount('');
    setFileLabel('');
    setBankId(DEMO.banks[0].value);
    if (fileRef.current) fileRef.current.value = '';
    onHide();
  };

  const handleSubmit = () => {
    const raw = creditAmount;
    if (raw != null && String(raw).trim() !== '') {
      const credit = parseFloat(String(raw).replace(/,/g, '').trim());
      if (Number.isFinite(credit) && credit > 0) {
        dispatch(addVanCreditAmount(credit));
      }
    }
    resetAndClose();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFileLabel(f?.name || '');
  };

  return (
    <Modal
      show={show}
      onHide={resetAndClose}
      size='lg'
      centered
      scrollable
      contentClassName='border-0 shadow'>
      <Modal.Header
        closeButton={false}
        className='border-0 pb-0 pt-3 px-4 flex-column align-items-stretch'>
        <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div className='d-flex align-items-center flex-wrap gap-2 gap-sm-3'>
            <Modal.Title className='fs-4 fw-normal text-secondary mb-0'>
              VAN Account
            </Modal.Title>
            <span className='text-muted fs--1 text-nowrap'>powered by</span>
            <img
              src={poweredByLogo}
              alt='EXIM BANK'
              className='d-block'
              style={{ height: 32, width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <button
            type='button'
            className='d-flex align-items-center justify-content-center border-0 rounded-1'
            style={{
              width: 28,
              height: 28,
              backgroundColor: ACCENT_ORANGE,
            }}
            aria-label='Close'
            onClick={resetAndClose}>
            <FontAwesomeIcon icon={faTimes} className='text-white fs-6' />
          </button>
        </div>
        <hr
          className='mt-2 mb-0 opacity-100'
          style={{ borderTopWidth: 2, borderColor: ACCENT_ORANGE }}
        />
      </Modal.Header>

      <Modal.Body className='pt-3 pb-3 px-3'>
        <Row className='g-4'>
          <Col md={6}>
            <div className='mb-3'>
              <div className='text-muted small mb-1'>VAN Account No.</div>
              <div className='text-dark fw-medium'>{DEMO.vanAccountNo}</div>
            </div>

            <div className='fw-bold text-dark mb-2'>
              Bank Details to Credit VAN
            </div>

            <Form.Group
              className='pb-3 pe-1 ps-3 mb-2'
              controlId='vanAccountModal.bank'>
              <Form.Label className='fs--1'>
                Bank
              </Form.Label>
              <Form.Select
                aria-label='Bank'
                className='fs--1'
                value={bankId}
                onChange={(e) => setBankId(e.target.value)}>
                {DEMO.banks.map((b) => (
                  <option key={b.value} value={b.value} className='text-dark'>
                    {b.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group
              className='pb-3 pe-1 ps-3 mb-2'
              controlId='vanAccountModal.creditAmount'>
              <Form.Label className='fs--1'>
                {`Amt to be credited (${vanCurrency})`}
              </Form.Label>
              <Form.Control
                type='text'
                inputMode='decimal'
                className='fs--1'
                placeholder={`Enter amount (${vanCurrency})`}
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </Form.Group>

            <dl className='mb-3 small'>
              <dt className='text-muted fw-normal mb-0'>Account Name</dt>
              <dd className='mb-2 text-dark'>{DEMO.accountName}</dd>
              <dt className='text-muted fw-normal mb-0'>Account No.</dt>
              <dd className='mb-2 text-dark text-break'>{DEMO.accountNo}</dd>
              <dt className='text-muted fw-normal mb-0'>Swift Address</dt>
              <dd className='mb-2 text-dark'>
                {DEMO.swiftAddress || '\u2014'}
              </dd>
              <dt className='text-muted fw-normal mb-0'>IBAN</dt>
              <dd className='mb-0 text-dark text-break'>{DEMO.iban}</dd>
            </dl>

            <Form.Group className='pb-3 pe-1 ps-3 mb-2'>
              <Form.Label className='fs--1 text-muted'>Upload Receipt</Form.Label>
              <div className='d-flex align-items-center flex-wrap gap-2'>
              <Button
                variant='primary'
                size='sm'
                className='text-uppercase fw-semibold px-3'
                onClick={() => fileRef.current?.click()}>
                Choose File
              </Button>
              <input
                ref={fileRef}
                type='file'
                className='d-none'
                onChange={handleFileChange}
              />
              <span
                className='flex-grow-1 text-muted pb-1 border-bottom flex-shrink-1 fs--1'
                style={{ minWidth: '120px', borderColor: '#adb5bd' }}>
                {fileLabel || 'Upload your file'}
              </span>
              </div>
            </Form.Group>
          </Col>

          <Col md={6}>
            <div className='mb-3'>
              <div className='text-muted small mb-1'>VAN Amount</div>
              <div className='text-dark fw-medium fs-5'>
                {vanAmountDisplay} {vanCurrency}
              </div>
            </div>

            <div className='fw-bold text-dark mb-2'>How to credit Account</div>
            <ol className='text-secondary ps-3 mb-0 small' style={{ lineHeight: 1.6 }}>
              {STEPS.map((step, i) => (
                <li key={i} className='mb-2'>
                  {step}
                </li>
              ))}
            </ol>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className='flex-column border-0 px-4 pb-4 pt-2'>
        <div
          className='text-center small mb-3 w-100'
          style={{ color: ACCENT_ORANGE }}>
          Facing issue reach out to us on {DEMO.supportEmail}
        </div>
        <div className='d-flex justify-content-center gap-3 flex-wrap w-100'>
          <Button
            variant='success'
            className='px-4 d-inline-flex align-items-center gap-2 text-uppercase fw-semibold'
            onClick={handleSubmit}>
            <FontAwesomeIcon icon={faCheck} />
            Submit
          </Button>
          <Button
            variant='danger'
            className='px-4 d-inline-flex align-items-center gap-2 text-uppercase fw-semibold'
            onClick={resetAndClose}>
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default VanAccountModal;
